/**
 * DataTable - Ant Design Table wrapper with pagination
 * Ported from D:\Food for thunderbee stock pages
 */
import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { Table } from "antd";
import Pagination from "../views/components/Pagination";

export default function DataTable({
  columns,
  dataSource = [],
  rowKey,
  loading = false,
  defaultPageSize = 100,
  pageSizeOptions = [10, 20, 50, 100],
  rowClassName,
  onRow,
  tableHeight,
  disablePagination = false,
  showOnlyCount = false,
  defaultToLastPage = false,
  alwaysScroll = false,
  initialPage = 1,
  initialPageSize,
  onPageChange: onPageChangeProp,
  onPageSizeChange: onPageSizeChangeProp,
  ...rest
}) {
  const { scroll: scrollFromRest, ...tableRest } = rest;
  const [currentPage, setCurrentPage] = useState(initialPage);
  const [pageSize, setPageSize] = useState(initialPageSize ?? defaultPageSize);
  const [scrollY, setScrollY] = useState(null);
  const rootRef = useRef(null);
  const footerRef = useRef(null);
  const idRef = useRef(`dt-${Math.random().toString(36).slice(2)}`);

  const paginatedData = useMemo(() => {
    if (disablePagination) {
      return dataSource.map((item, i) => ({ ...item, __globalIndex: i + 1 }));
    }
    const start = (currentPage - 1) * pageSize;
    return dataSource
      .slice(start, start + pageSize)
      .map((item, i) => ({ ...item, __globalIndex: start + i + 1 }));
  }, [dataSource, currentPage, pageSize, disablePagination]);

  const shouldScroll = useMemo(() => {
    if (tableHeight) return true;
    if (disablePagination) return true;
    if (alwaysScroll) return true;
    return (paginatedData?.length || 0) > 8;
  }, [tableHeight, paginatedData?.length, disablePagination, alwaysScroll]);

  const computeScrollY = useCallback(() => {
    if (tableHeight) {
      setScrollY(typeof tableHeight === "number" ? tableHeight : tableHeight);
      return;
    }
    if (!shouldScroll) {
      setScrollY(null);
      return;
    }
    if (typeof window === "undefined") return;
    const vh = window.innerHeight;
    const tableBoxTop = rootRef.current?.getBoundingClientRect().top ?? 0;
    const footerH = footerRef.current?.getBoundingClientRect().height ?? (disablePagination ? 20 : 80);
    const safety = 40;
    const headerH = 55;
    const y = Math.max(200, Math.floor(vh - tableBoxTop - footerH - safety - headerH));
    setScrollY(y);
  }, [tableHeight, shouldScroll, disablePagination]);

  useEffect(() => {
    const raf = requestAnimationFrame(computeScrollY);
    const onResize = () => requestAnimationFrame(computeScrollY);
    window.addEventListener("resize", onResize);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", onResize);
    };
  }, [computeScrollY]);

  useEffect(() => {
    computeScrollY();
  }, [computeScrollY, dataSource?.length, currentPage, pageSize]);

  useEffect(() => {
    if (!dataSource?.length) return;
    const totalPages = Math.max(1, Math.ceil(dataSource.length / pageSize));
    if (currentPage > totalPages) setCurrentPage(totalPages);
    else if (defaultToLastPage) setCurrentPage(totalPages);
  }, [dataSource?.length, pageSize, defaultToLastPage]);

  const scrollYNum = typeof scrollY === "string" ? 300 : (scrollY ?? 200);

  const mergedScroll = useMemo(() => {
    const yPart = shouldScroll && scrollYNum > 0 ? { y: scrollYNum } : {};
    const xPart =
      scrollFromRest && typeof scrollFromRest === "object" ? scrollFromRest : {};
    const out = { ...yPart, ...xPart };
    return Object.keys(out).length > 0 ? out : undefined;
  }, [shouldScroll, scrollYNum, scrollFromRest]);

  return (
    <div className={`data-table-wrapper ${idRef.current}`}>
      {shouldScroll && scrollYNum > 0 && (
        <style>{`.${idRef.current} .ant-table-body { min-height: ${scrollYNum}px; }`}</style>
      )}
      <div
        ref={rootRef}
        className="data-table-root"
        style={{
          height: shouldScroll && scrollY ? (typeof scrollY === "number" ? `${scrollY + 55}px` : `calc(${scrollY} + 55px)`) : "auto",
        }}
      >
        <Table
          columns={columns}
          dataSource={paginatedData}
          rowKey={rowKey}
          loading={loading}
          scroll={mergedScroll}
          pagination={false}
          rowClassName={rowClassName}
          onRow={onRow}
          size="middle"
          tableLayout="auto"
          {...tableRest}
        />
      </div>
      <div ref={footerRef} className="data-table-footer">
        {disablePagination || showOnlyCount ? (
          <span>
            จำนวนรายการ : <b>{dataSource?.length || 0}</b> รายการ
          </span>
        ) : (
          <Pagination
            totalItems={dataSource?.length || 0}
            itemsPerPage={pageSize}
            currentPage={currentPage}
            compact
            onPageChange={(page) => {
              setCurrentPage(page);
              onPageChangeProp?.(page);
            }}
            onItemsPerPageChange={(val) => {
              const n = Number(val);
              setCurrentPage(1);
              setPageSize(n);
              onPageSizeChangeProp?.(n);
            }}
          />
        )}
      </div>
    </div>
  );
}
